/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { type ReactNode, useEffect, useMemo } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { Paper } from '@atlassian/conversation-assistant-ui-components/ui/Paper';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import type { ConversationAssistantUIModes } from '../../../../types';

const styles = cssMap({
	actionViewContainer: {
		position: 'relative',
		overflow: 'hidden',
	},
	actionViewHeader: {
		alignSelf: 'flex-end',
		marginTop: token('space.150'),
		marginBottom: token('space.150'),
	},
	actionViewHeaderContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
});

export const ConversationActionView = ({
	action,
	mode: _mode,
	footer,
	hideActionHeader = false,
}: {
	action: ConversationAction;
	mode: ConversationAssistantUIModes;
	footer?: ReactNode;
	/** Hides the in-content action header bubble. */
	hideActionHeader?: boolean;
}): JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();

	const Icon = action.icon;
	const actionId = action.id;
	const renderActionView = action.viewConfig?.render;
	const isRenderStabilizationEnabled =
		FeatureGates.getExperimentValue<boolean>(
			'rovo_ttai_segment_render_stabilization',
			'isEnabled',
			false,
		) === true;
	// `viewConfig.render` is a pure render function by contract; the treatment caches
	// its output only while the render function identity is unchanged.
	const memoizedActionView = useMemo(
		() => (isRenderStabilizationEnabled ? renderActionView?.() : undefined),
		[isRenderStabilizationEnabled, renderActionView],
	);
	const actionView = isRenderStabilizationEnabled ? memoizedActionView : renderActionView?.();
	const analyticsActionIdentity = isRenderStabilizationEnabled ? actionId : action;
	const showActionViewHeader =
		!hideActionHeader && !('closePanelOnSelection' in action && action.closePanelOnSelection);

	useEffect(() => {
		const analyticsActionId =
			typeof analyticsActionIdentity === 'string'
				? analyticsActionIdentity
				: analyticsActionIdentity.id;

		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'aiMateActionView',
			attributes: {
				actionId: analyticsActionId,
			},
		});
	}, [analyticsActionIdentity, sendAnalyticsEvent]);

	return (
		<Stack>
			{showActionViewHeader && (
				<Box xcss={styles.actionViewHeader}>
					<Paper variant="accent" verticalSpace={false} pinchCorner="bottomRight">
						<Box xcss={styles.actionViewHeaderContent}>
							<Icon label="" primaryColor={token('color.icon.subtlest')} />
							<Inline>{action.title}</Inline>
						</Box>
					</Paper>
				</Box>
			)}
			<UFOSegment name={'conversation-action'}>
				<UFOCustomData data={{ conversationActionTitle: action.title }} />
				<Box xcss={styles.actionViewContainer}>{actionView}</Box>
			</UFOSegment>
			{footer}
		</Stack>
	);
};
